'use client';

import { useMemo } from 'react';

import type {
  ComplianceJurisdiction,
  FormFieldDescriptor,
} from '@selenos/leads';
import { zodToFormFields } from '@selenos/leads';
import { driversConfig } from '@selenos/vertical-drivers/config';
import {
  spitexDemandConfig,
  spitexSupplyConfig,
} from '@selenos/vertical-spitex/config';

/**
 * Client-side vertical config resolver.
 *
 * Imports use subpath exports (e.g. @selenos/vertical-drivers/config) to
 * avoid pulling in barrel exports that chain to server-only modules.
 */

type VerticalConfigLike = typeof driversConfig;

const VERTICAL_CONFIGS: Record<string, VerticalConfigLike> = {
  drivers: driversConfig,
  'spitex-demand': spitexDemandConfig as VerticalConfigLike,
  'spitex-supply': spitexSupplyConfig as VerticalConfigLike,
};

interface SerializableVerticalConfig {
  name: string;
  compliance: { jurisdiction: ComplianceJurisdiction };
}

const COUNTRY_PHONE_PLACEHOLDERS: Record<string, string> = {
  ZA: '+27 82 123 4567',
  CH: '+41 79 123 45 67',
  US: '+1 (555) 123-4567',
  DE: '+49 170 1234567',
  FR: '+33 6 12 34 56 78',
  GB: '+44 7911 123456',
};

export function useVerticalConfig(verticalSlug: string): {
  verticalConfig: SerializableVerticalConfig | null;
  customFormFields: FormFieldDescriptor[];
  phonePlaceholder: string | undefined;
  requirePhone: boolean;
} {
  return useMemo(() => {
    const config = VERTICAL_CONFIGS[verticalSlug];

    if (!config) {
      return {
        verticalConfig: null,
        customFormFields: [],
        phonePlaceholder: undefined,
        requirePhone: true,
      };
    }

    const verticalConfig: SerializableVerticalConfig = {
      name: config.name,
      compliance: { jurisdiction: config.compliance.jurisdiction },
    };

    const customFormFields = zodToFormFields(config.customFieldsSchema);

    const phonePlaceholder =
      config.landingPage?.phonePlaceholder ??
      COUNTRY_PHONE_PLACEHOLDERS[config.country] ??
      undefined;

    // Default to phone-required; verticals opt out explicitly.
    const requirePhone = config.requirePhone ?? true;

    return {
      verticalConfig,
      customFormFields,
      phonePlaceholder,
      requirePhone,
    };
  }, [verticalSlug]);
}
